import { Link } from 'react-router'
import type { JSX } from 'react'
import { useOnboarding } from '@/onboarding/context'
import { notConfigured } from '@/onboarding/steps'

/**
 * The reminder above every shell screen while setup is unfinished.
 *
 * S-D2's resume screen draws this banner on the dashboard, and it is the only
 * route back into the wizard once somebody has left it — without it, an
 * abandoned setup would be abandoned for good.
 *
 * **Four reasons it renders nothing**, and each is a real state rather than a
 * guard: the wizard has not loaded, there is nothing outstanding, the tenant
 * pressed *Don't remind me again*, or the wizard has never been started at all.
 * The last one matters — a brand-new organisation is redirected into the wizard
 * by `Organisations`, and a banner telling them to finish something they have
 * not begun would be the shell nagging about its own redirect.
 */
export function SetupBanner(): JSX.Element | null {
  const { state } = useOnboarding()
  if (!state) return null
  if (state.dismissed_at) return null

  // "Not done", not `progress.outstanding`: a tenant who skipped three steps has
  // settled them as far as the wizard is concerned and has three things still
  // switched off, which is exactly what this banner is for (`notConfigured`).
  const remaining = notConfigured(state).length
  const configured = state.steps.length - remaining
  if (remaining === 0) return null
  if (configured === 0) return null

  return (
    <div className="ar-alert ar-alert--info app-setup-banner">
      <span className="ar-alert__icon" aria-hidden="true">
        ◷
      </span>
      <div className="ar-alert__body">
        <strong>
          Setup is {configured} of {state.steps.length} done
        </strong>
        {remaining === 1 ? 'One step' : `${String(remaining)} steps`} left — some of what AugRank
        can do is still switched off.
      </div>
      <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/onboarding/resume">
        Pick up where you left off
      </Link>
    </div>
  )
}
